import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { fmtDelta, fmtDec, fmtInt, fmtMYRShort, fmtPct } from "@/lib/format";

export const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.3, delay: i * 0.04, ease: "easeOut" } }),
};

export const formatKpi = (kpi) => {
  if (!kpi || kpi.value === null || kpi.value === undefined) return "—";
  switch (kpi.format) {
    case "pct":
      return fmtPct(kpi.value, 1);
    case "myr":
      return fmtMYRShort(kpi.value);
    case "dec":
      return fmtDec(kpi.value, 1);
    default:
      return fmtInt(kpi.value);
  }
};

export const deltaTone = (kpi) => {
  if (!kpi || kpi.delta === null || kpi.delta === undefined) return "neutral";
  if (kpi.good === "neutral") return "info";
  const positive = kpi.delta > 0;
  return (kpi.good === "down" ? !positive : positive) ? "positive" : "negative";
};

const TONE = {
  positive: "text-success border-success/30 bg-success/10",
  negative: "text-danger border-danger/30 bg-danger/10",
  warning: "text-warning border-warning/30 bg-warning/10",
  info: "text-info border-info/30 bg-info/10",
  neutral: "text-muted-foreground border-border/70 bg-secondary/60",
};

export const DeltaBadge = ({ delta, kind = "pct", tone = "neutral", label, className, testId }) => {
  const txt = fmtDelta(delta, kind);
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-md border", TONE[tone] || TONE.neutral, className)} data-testid={testId || "kpi-tile-delta"}>
      {txt ?? "no comparison"}
      {label && <span className="text-muted-foreground/80 hidden sm:inline">{label}</span>}
    </span>
  );
};

/** Headline KPI tile with like-for-like delta. `kpi` = {value, prev, delta, delta_kind, format, good}. */
export const KpiTile = ({ label, kpi, value, sub, hint, index = 0, testId, icon: Icon, compareLabel, note }) => {
  const display = value ?? formatKpi(kpi);
  const tone = deltaTone(kpi);
  return (
    <motion.div variants={fadeUp} initial="hidden" animate="show" custom={index} data-testid={testId || "kpi-tile"} className="card-surface card-hover p-4 md:p-5 flex flex-col gap-3 min-h-[128px] relative overflow-hidden">
      <span className="absolute inset-x-4 top-0 h-px bg-gold/25" />
      <div className="flex items-start justify-between gap-2">
        <div className="eyebrow">{label}</div>
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={15} className="text-gold/80" strokeWidth={1.75} />}
          {hint && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="What this means" className="text-muted-foreground/70 hover:text-foreground focus-gold rounded"><Info size={14} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">{hint}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="font-display text-2xl md:text-[30px] tabular leading-none" data-testid="kpi-tile-value">{display}</div>
      <div className="flex items-center gap-2 flex-wrap mt-auto">
        {kpi && kpi.delta !== undefined && <DeltaBadge delta={kpi.delta} kind={kpi.delta_kind} tone={tone} label={compareLabel} />}
        {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
      </div>
      {note && <div className="text-[11px] text-muted-foreground -mt-1">{note}</div>}
    </motion.div>
  );
};

export const KpiSkeleton = ({ n = 4 }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="card-surface p-4 md:p-5 min-h-[128px] space-y-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-20" />
      </div>
    ))}
  </div>
);
